/**
 * @file PersonalTab.js
 * @description This component displays and manages personal information for an individual including personal details, address, and contact information. Supports both create and edit modes.
 */

import {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    useCallback,
    useRef,
} from 'react';
import { getUsStatesTerritories, parsePhoneNumber } from '../../utils/utils';
import { Dropdown } from '../helpers/Dropdown';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import PhoneInput from 'react-phone-number-input/input';
import Toast from '../ui/toasts/Toast';
import { updateIndividual } from '../../services/individualService';
import { fetchGenders } from '../../services/systemService';
import { isValidEmailAddress, isValidPhoneNumber } from '../../utils/validate';
import style from './IndividualPage.module.scss';
import { TenantUserStatusEnum } from '../../utils/data/enums';
import { getText } from '../../i18n';

const SSN_MASK_COUNT = 5;
const SSN_MASK_DELAY = 1000;

/**
 * Checks if the account is disabled and shows toast message if so
 * @param {Object} tenantUser - The tenant user object
 * @returns {boolean} True if account is disabled
 */
const checkAccountDisabled = tenantUser => {
    if (tenantUser?.tenantUserStatusId === TenantUserStatusEnum.Disabled) {
        Toast.error(
            getText('msg_66aa8e9b0915'),
            {},
            getText('msg_8d884d6c8bcb'),
        );
        return true;
    }
    return false;
};

/**
 * Normalizes a single phone number string to the 10-digit bare format the
 * API expects (e.g. "+12025550100" or "+2025550100" → "2025550100").
 * @param {string} value - The phone number string
 * @returns {string} The normalized phone number
 */
const normalizePhoneNumberValue = value => {
    if (!value) return value;
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
    if (digits.length === 10) return digits;
    return digits || value;
};

/**
 * Normalizes phone number fields in the data payload back to the 10-digit
 * bare format the API expects (e.g. "+12025550100" → "2025550100").
 * Also normalizes phone numbers inside nested tenantUsers entries.
 * @param {object} data - The data payload
 * @returns {object} The updated data payload with phone numbers in API format
 */
const removePlusFromPhoneNumbers = data => {
    const updatedData = { ...data };
    Object.keys(updatedData).forEach(key => {
        if (key.includes('PhoneNumber') && updatedData[key]) {
            updatedData[key] = normalizePhoneNumberValue(updatedData[key]);
        }
    });
    if (Array.isArray(updatedData.tenantUsers)) {
        updatedData.tenantUsers = updatedData.tenantUsers.map(tu => {
            const normalizedTu = { ...tu };
            Object.keys(normalizedTu).forEach(key => {
                if (key.includes('PhoneNumber') && normalizedTu[key]) {
                    normalizedTu[key] = normalizePhoneNumberValue(
                        normalizedTu[key],
                    );
                }
            });
            return normalizedTu;
        });
    }
    return updatedData;
};

/**
 * Checks if form has minimum required fields filled
 * @param {object} data - The form data
 * @returns {boolean} True if form has required fields
 */
const checkFormValidity = data => {
    const hasFirstName = data.firstName?.trim();
    const hasLastName = data.lastName?.trim();
    const hasUniqueId = data.uniqueId?.trim();
    const hasEmail = data.emailAddress && data.emailAddress.trim() !== '';
    const hasPhone =
        data.mobilePhoneNumber && data.mobilePhoneNumber.trim() !== '';
    const hasContactMethod = hasEmail || hasPhone;

    return !!(hasFirstName && hasLastName && hasUniqueId && hasContactMethod);
};

/**
 * Builds a display string for the SSN field using rolling masking.
 *
 * The raw value contains only digits (no dashes). We walk each character and
 * decide whether it should be shown as-is or replaced with '*':
 *   – Digit positions 0-4 (the first 5 digits) are masked UNLESS their index
 *     is in the `revealedIndices` Set, meaning the mask timer hasn't fired yet.
 *   – Digit positions 5+ are always shown in the clear.
 *
 * @param {string} rawValue   - The real SSN digits (no formatting chars)
 * @param {Set<number>} revealedIndices - Indices of digits whose mask timer is still pending
 * @returns {string} The display string shown in the input
 */
const buildSsnDisplay = (rawValue, revealedIndices) => {
    if (!rawValue) return '';

    let display = '';
    for (let i = 0; i < rawValue.length; i++) {
        if (i < SSN_MASK_COUNT) {
            display += revealedIndices.has(i) ? rawValue[i] : '*';
        } else {
            display += rawValue[i];
        }
    }
    return display;
};

const PersonalTab = forwardRef(
    (
        {
            individualId,
            individualRecord,
            tenantUser,
            onSave,
            onDataChange,
            fieldNamingMap,
            defaultFieldNamingMap,
            enableEmailAndPhoneValidation,
            isCreateMode = false,
        },
        ref,
    ) => {
        const [formData, setFormData] = useState({
            firstName: '',
            middleName: '',
            lastName: '',
            birthday: null,
            uniqueId: '',
            ssn: '',
            address: '',
            address2: '',
            city: '',
            stateAbbreviation: '',
            zip: '',
            genderId: null,
            emailAddress: '',
            mobilePhoneNumber: '',
        });

        const [isLoading, setIsLoading] = useState(false);
        const [usStatesTerritories, setUsStatesTerritories] = useState([]);
        const [errors, setErrors] = useState({});
        const [ssnEdited, setSsnEdited] = useState(false);
        const [genders, setGenders] = useState([]);
        const [defaultGender, setDefaultGender] = useState(null);

        // ── SSN rolling-mask state ──────────────────────────────────────
        // The actual digits the user has typed (no dashes, no asterisks).
        const [rawSsn, setRawSsn] = useState('');
        // Set of digit-indices (0-based) whose mask timer hasn't fired yet,
        // so they should still be shown in the clear.
        const [revealedIndices, setRevealedIndices] = useState(new Set());
        // Map of digit-index → setTimeout id so we can cancel on unmount or
        // when the user clears/backspaces.
        const maskTimers = useRef({});

        // Clean up any pending timers on unmount
        useEffect(() => {
            return () => {
                Object.values(maskTimers.current).forEach(clearTimeout);
            };
        }, []);

        useEffect(() => {
            const states = [
                { label: getText('msg_1bcf1fee269d'), value: '' },
                ...getUsStatesTerritories().map(e => ({
                    label: e.name,
                    value: e.acronym,
                })),
            ];
            setUsStatesTerritories(states);

            loadGenders();
        }, []);

        useEffect(() => {
            if (!individualRecord) return;

            if (isCreateMode) {
                setFormData(prev => ({
                    ...prev,
                    ...individualRecord,
                }));
                return;
            }

            setFormData({
                ...individualRecord,
                ssn: null,
                displaySSN: individualRecord.displaySSN,
                mobilePhoneNumber: parsePhoneNumber(
                    individualRecord.mobilePhoneNumber,
                ),
            });
        }, [individualRecord, isCreateMode]);

        const notifyParentOfChange = useCallback(
            newFormData => {
                if (isCreateMode && onDataChange) {
                    const isValid = checkFormValidity(newFormData);
                    onDataChange(newFormData, isValid);
                }
            },
            [isCreateMode, onDataChange],
        );

        const handleInputChange = event => {
            const { name, value } = event.target;

            // SSN is handled separately via handleSsnChange
            if (name === 'ssn') return;

            const updatedFormData = {
                ...formData,
                [name]: value === '' ? null : value,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);

            if (errors[name]) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [name]: null,
                }));
            }
        };

        // ── SSN input handler with rolling mask ─────────────────────────
        const handleSsnChange = event => {
            const inputValue = event.target.value;
            setSsnEdited(true);

            // The display string contains '*' for masked positions and real
            // digits for revealed / tail positions. To figure out what the
            // user actually typed we need to reconcile against the previous
            // raw value.
            //
            // Strategy: walk the new display value char-by-char. For each
            // position keep '*' → carry over the existing rawSsn digit at
            // that index. Otherwise take the literal character (which is a
            // new digit the user typed or an unmasked tail digit).

            // Strip any non-digit, non-asterisk characters (prevent pasting dashes, letters, etc.)
            const cleaned = inputValue.replace(/[^0-9*]/g, '');

            let newRaw = '';
            for (let i = 0; i < cleaned.length; i++) {
                if (cleaned[i] === '*') {
                    // Carry over the already-stored digit at this position
                    newRaw += rawSsn[i] ?? '';
                } else {
                    newRaw += cleaned[i];
                }
            }

            // Enforce max length of 11 (standard SSN = 9, but original code
            // allowed maxLength 11 to account for dashes — we keep the same
            // ceiling on raw digit count for parity with the original UX
            // which displayed extra chars unmasked).
            if (newRaw.length > 11) {
                newRaw = newRaw.slice(0, 11);
            }

            // Cancel timers for indices that no longer exist (user backspaced)
            Object.keys(maskTimers.current).forEach(idx => {
                if (Number(idx) >= newRaw.length) {
                    clearTimeout(maskTimers.current[idx]);
                    delete maskTimers.current[idx];
                }
            });

            // Determine which indices are newly revealed (just typed)
            const newRevealed = new Set();

            // Preserve any still-pending reveals that are within range
            revealedIndices.forEach(idx => {
                if (idx < newRaw.length && idx < SSN_MASK_COUNT) {
                    newRevealed.add(idx);
                }
            });

            // Detect newly added digit positions (user just typed them)
            for (let i = rawSsn.length; i < newRaw.length; i++) {
                if (i < SSN_MASK_COUNT) {
                    newRevealed.add(i);

                    // Schedule masking after delay
                    if (maskTimers.current[i]) {
                        clearTimeout(maskTimers.current[i]);
                    }
                    const idx = i;
                    maskTimers.current[idx] = setTimeout(() => {
                        setRevealedIndices(prev => {
                            const next = new Set(prev);
                            next.delete(idx);
                            return next;
                        });
                        delete maskTimers.current[idx];
                    }, SSN_MASK_DELAY);
                }
            }

            setRawSsn(newRaw);
            setRevealedIndices(newRevealed);

            // Keep formData.ssn in sync so the save payload is correct
            const updatedFormData = {
                ...formData,
                ssn: newRaw || null,
            };
            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const handleDateChange = date => {
            const updatedFormData = {
                ...formData,
                birthday: date,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const handlePhoneChange = value => {
            const updatedFormData = {
                ...formData,
                mobilePhoneNumber: value || null,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);

            if (errors.mobilePhoneNumber) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    mobilePhoneNumber: null,
                }));
            }
        };

        const handleDropdownChange = (fieldName, value) => {
            const updatedFormData = {
                ...formData,
                [fieldName]: value === '' ? null : value,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const handleGenderChange = value => {
            const updatedFormData = {
                ...formData,
                genderId: value,
            };

            setFormData(updatedFormData);
            notifyParentOfChange(updatedFormData);
        };

        const getFieldLabel = defaultLabel => {
            const mappingKey = defaultFieldNamingMap?.[defaultLabel];
            if (
                mappingKey &&
                fieldNamingMap?.[mappingKey] &&
                fieldNamingMap[mappingKey] !== defaultLabel
            ) {
                return {
                    label: fieldNamingMap[mappingKey],
                    subLabel: `(${defaultLabel})`,
                };
            }
            return { label: defaultLabel, subLabel: '' };
        };

        const maskDisplaySSN = ssn => {
            if (!ssn) return ssn;
            const cleaned = ssn.replace(/[-\s]/g, '');
            return cleaned.replace(/[Xx]/g, '*');
        };

        const validateForm = () => {
            const validationErrors = {};

            if (!formData.firstName?.trim()) {
                validationErrors.firstName = 'First Name is required.';
            }

            if (!formData.lastName?.trim()) {
                validationErrors.lastName = 'Last Name is required.';
            }

            if (!formData.uniqueId?.trim()) {
                validationErrors.uniqueId = 'Unique ID is required.';
            }

            if (formData.birthday) {
                const parsedBirthday = new Date(formData.birthday);
                const year = parsedBirthday.getFullYear();
                if (
                    Number.isNaN(parsedBirthday.getTime()) ||
                    year < 1000 ||
                    year > 9999
                ) {
                    validationErrors.birthday =
                        'Please enter a valid birthdate.';
                }
            }

            const hasEmail =
                formData.emailAddress && formData.emailAddress.trim() !== '';
            const hasPhone =
                formData.mobilePhoneNumber &&
                formData.mobilePhoneNumber.trim() !== '';

            if (
                !hasEmail &&
                !hasPhone &&
                enableEmailAndPhoneValidation === 'true'
            ) {
                validationErrors.emailAddress =
                    'Either email address or phone number is required.';
                validationErrors.mobilePhoneNumber =
                    'Either email address or phone number is required.';
            }

            if (hasEmail && !isValidEmailAddress(formData.emailAddress)) {
                validationErrors.emailAddress = 'Invalid email address.';
            }

            if (hasPhone && !isValidPhoneNumber(formData.mobilePhoneNumber)) {
                validationErrors.mobilePhoneNumber = 'Invalid phone number.';
            }

            setErrors(validationErrors);

            if (Object.keys(validationErrors).length > 0) {
                const firstError = Object.values(validationErrors)[0];
                Toast.error(firstError);
                return false;
            }

            return true;
        };

        const loadGenders = async () => {
            try {
                const gendersResponse = await fetchGenders();
                setGenders(gendersResponse);
                setDefaultGender(gendersResponse.find(g => g.isDefault));
            } catch (error) {
                console.error('Could not fetch genders', error);
            }
        };

        const handleSave = async event => {
            event?.preventDefault();

            if (!isCreateMode && checkAccountDisabled(tenantUser)) return;

            if (!validateForm()) return;

            if (isCreateMode) {
                return true;
            }

            try {
                setIsLoading(true);

                const payload = {
                    ...individualRecord,
                    ...removePlusFromPhoneNumbers(formData),
                };
                if (ssnEdited) {
                    if (formData.ssn) {
                        payload.ssn = formData.ssn.replace(/\D/g, '');
                    } else {
                        payload.ssn = null;
                    }
                } else if (individualRecord?.ssn) {
                    payload.ssn = individualRecord.ssn;
                }

                await updateIndividual(payload);
                Toast.success(getText('msg_fee515ed0b9d'));
                onSave();
            } catch (error) {
                console.error('Unable to save changes', error);
                if (error?.response?.data?.detail) {
                    Toast.error(error.response.data.detail);
                } else {
                    Toast.error(getText('msg_3c2ce6f3c1a9'));
                }
            } finally {
                setIsLoading(false);
            }
        };

        useImperativeHandle(ref, () => ({
            handleSave,
            validateForm,
            getFormData: () => removePlusFromPhoneNumbers(formData),
        }));

        const uniqueIdLabel = getFieldLabel('Unique ID');
        const emailDefaultLabels = Object.keys(defaultFieldNamingMap || {});
        const emailDefaultLabel =
            emailDefaultLabels.find(
                defaultLabel =>
                    defaultFieldNamingMap[defaultLabel] === 'EmailAddress',
            ) ||
            emailDefaultLabels.find(
                defaultLabel => defaultFieldNamingMap[defaultLabel] === 'Email',
            );
        const emailFieldLabel = emailDefaultLabel
            ? getFieldLabel(emailDefaultLabel)
            : null;
        const emailAddressLabel =
            emailFieldLabel?.label !== emailDefaultLabel
                ? emailFieldLabel?.label
                : getText('msg_2eb4eefd0c25');

        // ── Compute the SSN display value ───────────────────────────────
        // When the user has started editing, show the rolling-mask view.
        // Otherwise fall back to the server-provided masked display.
        const ssnDisplayValue = ssnEdited
            ? buildSsnDisplay(rawSsn, revealedIndices)
            : formData.displaySSN
              ? maskDisplaySSN(formData.displaySSN)
              : formData.ssn || '';

        return (
            <form className={style.personalTabContainer}>
                {/* Personal Details Section */}
                <div className="d-flex mx-0 mt-4 mb-4">
                    <h5 className="pr-2 me-3 text-nowrap">
                        {getText('msg_1aec6cffe9f1')}
                    </h5>
                    <hr className={`w-100 ${style.sectionDivider}`} />
                </div>

                <div className="row px-5 pb-3 ms-0 ms-lg-5">
                    {/* Row 1: First Name, Middle, Last Name */}
                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="firstName">
                            {getText('msg_b6ea992aab46')}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            id="firstName"
                            type="text"
                            maxLength="35"
                            className={`form-control ${
                                errors.firstName ? 'is-invalid' : ''
                            }`}
                            value={formData.firstName || ''}
                            onChange={handleInputChange}
                            name="firstName"
                        />
                    </div>

                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="middleName">
                            {getText('msg_ced02810fc9e')}
                        </label>
                        <input
                            id="middleName"
                            type="text"
                            maxLength="35"
                            className="form-control"
                            value={formData.middleName || ''}
                            onChange={handleInputChange}
                            name="middleName"
                        />
                    </div>

                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="lastName">
                            {getText('msg_863cb39fbe7d')}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            id="lastName"
                            type="text"
                            maxLength="50"
                            className={`form-control ${
                                errors.lastName ? 'is-invalid' : ''
                            }`}
                            value={formData.lastName || ''}
                            onChange={handleInputChange}
                            name="lastName"
                        />
                    </div>

                    {/* Row 2: Birthdate, Unique ID, SSN */}
                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="birthday">
                            {getText('msg_ab5815528f10')}
                        </label>
                        <CustomDatePicker
                            selectedDate={formData.birthday}
                            onDateChange={handleDateChange}
                            errorMessage={errors.birthday}
                            hideErrorMessage={true}
                        />
                    </div>

                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="uniqueId">
                            {uniqueIdLabel.label}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            id="uniqueId"
                            type="text"
                            maxLength="256"
                            className={`form-control ${
                                errors.uniqueId ? 'is-invalid' : ''
                            }`}
                            value={formData.uniqueId || ''}
                            onChange={handleInputChange}
                            name="uniqueId"
                        />
                    </div>

                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="ssn">
                            {getText('msg_77976892436b')}
                        </label>
                        <input
                            id="ssn"
                            type="text"
                            maxLength="11"
                            className={`form-control ${style.ssnInput}`}
                            value={ssnDisplayValue}
                            onChange={handleSsnChange}
                            name="ssn"
                            autoComplete="off"
                        />
                    </div>

                    {/* Row 3: Address, Apt/Suite, City */}
                    <div className="col-12 col-md-5 mb-3">
                        <label htmlFor="address">
                            {getText('msg_d70f93df5e8f')}
                        </label>
                        <input
                            id="address"
                            type="text"
                            maxLength="100"
                            className="form-control"
                            value={formData.address || ''}
                            onChange={handleInputChange}
                            name="address"
                        />
                    </div>

                    <div className="col-12 col-md-3 mb-3">
                        <label htmlFor="address2" className="invisible">
                            {getText('msg_33fa7f8f9312')}
                        </label>
                        <input
                            id="address2"
                            type="text"
                            maxLength="50"
                            className="form-control"
                            value={formData.address2 || ''}
                            onChange={handleInputChange}
                            name="address2"
                            placeholder={getText('msg_33fa7f8f9312')}
                        />
                    </div>

                    <div className="col-12 col-md-4 mb-3">
                        <label htmlFor="city">
                            {getText('msg_4271627f4f0b')}
                        </label>
                        <input
                            id="city"
                            type="text"
                            maxLength="50"
                            className={`form-control ${
                                errors.city ? 'is-invalid' : ''
                            }`}
                            value={formData.city || ''}
                            onChange={handleInputChange}
                            name="city"
                        />
                    </div>

                    {/* Row 4: State, ZIP, Country, Gender */}
                    <div className="col-6 col-md-3 mb-3">
                        <label htmlFor="stateAbbreviation">
                            {getText('msg_a72502067518')}
                        </label>
                        <Dropdown
                            name="stateAbbreviation"
                            defaultValue={
                                usStatesTerritories.find(
                                    state =>
                                        state.value ===
                                        formData.stateAbbreviation,
                                )?.label || 'Select State'
                            }
                            options={usStatesTerritories}
                            onChange={value =>
                                handleDropdownChange('stateAbbreviation', value)
                            }
                            value={formData.stateAbbreviation || ''}
                            className={
                                errors.stateAbbreviation ? 'is-invalid' : ''
                            }
                        />
                    </div>

                    <div className="col-6 col-md-2 mb-3">
                        <label htmlFor="zip">
                            {getText('msg_d9d220ab2925')}
                        </label>
                        <input
                            id="zip"
                            type="text"
                            maxLength="10"
                            className={`form-control ${
                                errors.zip ? 'is-invalid' : ''
                            }`}
                            value={formData.zip || ''}
                            onChange={handleInputChange}
                            name="zip"
                        />
                    </div>

                    <div className="col-12 col-md-5 mb-3">
                        <label htmlFor="gender">
                            {getText('msg_8a754c61c2ce')}
                        </label>
                        <div className="d-flex align-items-center mt-2 flex-nowrap">
                            {genders.map(option => (
                                <div
                                    key={String(option.id)}
                                    className="form-check me-4 d-flex align-items-center"
                                >
                                    <input
                                        type="radio"
                                        id={`gender-${option.id}`}
                                        name="genderId"
                                        value={option.id}
                                        checked={
                                            (formData.genderId ??
                                                defaultGender?.id) === option.id
                                        }
                                        onChange={() =>
                                            handleGenderChange(option.id)
                                        }
                                        className="form-check-input me-2"
                                    />
                                    <label
                                        htmlFor={`gender-${option.id}`}
                                        className="form-check-label text-nowrap align-middle mb-0"
                                    >
                                        {option.name}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Contact Section */}
                <div className="d-flex mx-0 mt-4 mb-4">
                    <h5 className="pr-2 me-3 text-nowrap">
                        {getText('msg_b37456c4530b')}
                    </h5>
                    <hr className={`w-100 ${style.sectionDivider}`} />
                </div>

                <div className="row px-5 pb-3 ms-0 ms-lg-5">
                    <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="emailAddress">
                            <i className="bi bi-envelope me-2" />
                            {emailAddressLabel}
                        </label>
                        <input
                            id="emailAddress"
                            type="email"
                            maxLength="254"
                            className={`form-control ${
                                errors.emailAddress ? 'is-invalid' : ''
                            }`}
                            value={formData.emailAddress || ''}
                            onChange={handleInputChange}
                            name="emailAddress"
                        />
                        {errors.emailAddress && (
                            <div className="invalid-feedback">
                                {errors.emailAddress}
                            </div>
                        )}
                    </div>

                    <div className="col-12 col-md-6 mb-3">
                        <label htmlFor="mobilePhoneNumber">
                            <i className="bi bi-telephone me-2" />
                            {getText('msg_f48c45453132')}
                        </label>
                        <PhoneInput
                            id="mobilePhoneNumber"
                            className={`form-control ${
                                errors.mobilePhoneNumber ? 'is-invalid' : ''
                            }`}
                            value={formData.mobilePhoneNumber || ''}
                            onChange={handlePhoneChange}
                            name="mobilePhoneNumber"
                            country="US"
                        />
                        {errors.mobilePhoneNumber && (
                            <div className="invalid-feedback">
                                {errors.mobilePhoneNumber}
                            </div>
                        )}
                    </div>
                </div>
            </form>
        );
    },
);

export default PersonalTab;
